import { Form } from 'react-bootstrap';
import { Dropdown } from '@Components/Dropdown';
import { getTimeZoneAbbr, generateTimeOptions } from '@src/utils/date-utils';
import {
  FREQUENCY_TYPE_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  DAY_OF_MONTH_OPTIONS
} from './anomalyDetectionConstants';
import { matchOptionValue } from './utils/alertUtils';

const timeZoneAbbr = getTimeZoneAbbr();
const timeOptions = generateTimeOptions();

export interface FrequencyData {
  type: 'hourly' | 'daily' | 'weekly' | 'monthly';
  time: string;
  dayOfWeek: string;
  dayOfMonth: string;
}

interface FrequencySectionProps {
  frequencyData: FrequencyData;
  onFrequencyChange: (field: keyof FrequencyData, value: string) => void;
  disabled?: boolean;
  label?: string;
}

export const FrequencySection = ({
  frequencyData,
  onFrequencyChange,
  disabled = false,
  label = 'Frequency Type:'
}: FrequencySectionProps) => (
  <div className='form-section'>
    <div className='frequency-row'>
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Dropdown
          searchable
          ariaLabel='Frequency type'
          placeholder='Select frequency type...'
          options={FREQUENCY_TYPE_OPTIONS}
          value={matchOptionValue(FREQUENCY_TYPE_OPTIONS, frequencyData.type)}
          onChange={type => onFrequencyChange('type', type ?? '')}
          disabled={disabled}
          reactSelectProps={{ noOptionsMessage: () => 'No frequency types available' }}
        />
      </Form.Group>

      {frequencyData.type === 'daily' && (
        <TimeGroup
          frequencyData={frequencyData}
          onFrequencyChange={onFrequencyChange}
          disabled={disabled}
        />
      )}

      {frequencyData.type === 'weekly' && (
        <>
          <Form.Group>
            <Form.Label>Day of Week:</Form.Label>
            <Dropdown
              searchable
              ariaLabel='Day of week'
              placeholder='Day'
              options={DAY_OF_WEEK_OPTIONS}
              value={matchOptionValue(DAY_OF_WEEK_OPTIONS, frequencyData.dayOfWeek)}
              onChange={dayOfWeek => onFrequencyChange('dayOfWeek', dayOfWeek ?? '')}
              disabled={disabled}
              reactSelectProps={{ noOptionsMessage: () => 'No day of week options available' }}
            />
          </Form.Group>
          <TimeGroup
            frequencyData={frequencyData}
            onFrequencyChange={onFrequencyChange}
            disabled={disabled}
          />
        </>
      )}

      {frequencyData.type === 'monthly' && (
        <>
          <Form.Group>
            <Form.Label>Day of Month:</Form.Label>
            <Dropdown
              searchable
              ariaLabel='Day of month'
              placeholder='Select day of month...'
              options={DAY_OF_MONTH_OPTIONS}
              value={matchOptionValue(DAY_OF_MONTH_OPTIONS, frequencyData.dayOfMonth)}
              onChange={dayOfMonth => onFrequencyChange('dayOfMonth', dayOfMonth ?? '')}
              disabled={disabled}
              reactSelectProps={{ noOptionsMessage: () => 'No day of month options available' }}
            />
          </Form.Group>
          <TimeGroup
            frequencyData={frequencyData}
            onFrequencyChange={onFrequencyChange}
            disabled={disabled}
          />
        </>
      )}
    </div>
  </div>
);

interface TimeGroupProps {
  frequencyData: FrequencyData;
  onFrequencyChange: (field: keyof FrequencyData, value: string) => void;
  disabled: boolean;
}

const TimeGroup = ({ frequencyData, onFrequencyChange, disabled }: TimeGroupProps) => (
  <Form.Group>
    <Form.Label>Time ({timeZoneAbbr}):</Form.Label>
    <Dropdown
      searchable
      ariaLabel='Time'
      placeholder='Select time...'
      options={timeOptions}
      value={matchOptionValue(timeOptions, frequencyData.time)}
      onChange={time => onFrequencyChange('time', time ?? '')}
      disabled={disabled}
      reactSelectProps={{ noOptionsMessage: () => 'No time options available' }}
    />
  </Form.Group>
);
